import React, { useCallback, useMemo } from "react";
import MapboxDraw, { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";

import MultiLayerEditPanelContent from "fond/draw/MultiLayerEdit/MultiLayerEditPanelContent";
import { MultiLayerEditProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProvider";
import {
  DeleteButton,
  PointButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  ToolbarConfig,
  UndoButton,
} from "fond/map/Toolbar";
import { MuPowerEditableLayerIds, MuPowerInputLayerIds, MuPowerSolverLayerIds } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { useStackedNavigationContext } from "fond/widgets";

const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [
      <SelectButton key="select" />,
      <PolygonSelectButton key="selectPolygon" />,
      <PolygonSelectButton key="selectLasso" selectionMode="lasso" />,
    ],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<PointButton key="drawPoint" />],
  },
  {
    id: "miscTools",
    type: "action",
    items: [<SnappingButton key="snapping" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

const snapTargetLayerIds: string[] = [MuPowerSolverLayerIds.sourceHubs, MuPowerEditableLayerIds.ugPoints, MuPowerInputLayerIds.poles];

const MuPowerSourceHubsDraw: React.FC = () => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { clear } = useStackedNavigationContext();

  const snapGetFeatures = useCallback((m: mapboxgl.Map, draw: MapboxDraw) => {
    return draw.getAll().features.filter((f) => snapTargetLayerIds.includes(f.properties?.layerId));
  }, []);

  const options: MapboxDrawOptions = useMemo(
    () => ({
      snap: true,
      snapOptions: {
        snapPx: 20,
        snapToMidPoints: false,
        snapVertexPriorityDistance: 0.000025,
        snapGetFeatures,
      },
      editOptions: {
        allLayers: [
          {
            ID: MuPowerSolverLayerIds.sourceHubs,
            GeometryType: "Point",
            getFeatureProperties: () => ({ layerId: MuPowerSolverLayerIds.sourceHubs }),
          },
          {
            ID: MuPowerEditableLayerIds.ugPoints,
            GeometryType: "Point",
            Hide: true,
          },
          {
            ID: MuPowerInputLayerIds.poles,
            GeometryType: "Point",
            Hide: true,
          },
          {
            ID: MuPowerEditableLayerIds.ugPath,
            GeometryType: "LineString",
            Hide: true,
          },
        ],
        editingLayerId: MuPowerSolverLayerIds.sourceHubs,
        editingLayerIds: [MuPowerSolverLayerIds.sourceHubs],
      },
    }),
    [snapGetFeatures]
  );

  return (
    <MultiLayerEditProvider versionId={versionId} options={options} config={toolbarConfig}>
      <MultiLayerEditPanelContent title="Edit source hubs" alertTitle="Edit source hubs" onFinish={clear} />
    </MultiLayerEditProvider>
  );
};

export default MuPowerSourceHubsDraw;
